import { FunnelFieldLabels } from '../../../constants/deprecated_fieldsManagementConsts';
import { ObjectTypeLabelAndKeyTag } from '../../common/ObjectTypeLabelAndKey';
import { DataTableRow } from '../../common/table/TableTypes';
import { TableTypography } from '../../common/table/utils';
import {
  ConfigurationType,
  DependenciesContentViewType,
  SelectedDependencyWithClearHistoryProps,
} from '../dependencies/types';
import { TableWithHeader, TableWithHeaderProps } from './shared/TableWithHeader';
import { useCallback, useMemo } from 'react';
import { createStableLabelWrapper, markStyles } from '../dependencies/search/LabelWrapperComponent';
import { PossibleSectionInDependencies } from '../dependencies/search/types';
import { TableItemHighlightedText } from '../dependencies/search/highlight/TableItemHighlightedText';
import { ConfigurationItemField, ConfigurationItemRollup } from '../../parser/ParserTypes';
import { FieldLabelWithNameInTooltip } from '../shared/FieldLabelWithNameInTooltip';

interface FieldTableProps extends Pick<TableWithHeaderProps, 'headerVariant' | 'tableHeader'> {
  items: (ConfigurationItemField | ConfigurationItemRollup)[];
  onFieldClick: (props: SelectedDependencyWithClearHistoryProps) => void;
  activeItemId?: string;
  clearHistory?: boolean;
  section?: PossibleSectionInDependencies;
  dataTableType?: string;
}

const fieldsColumns = [
  { field: 'label', headerName: 'Label', width: '140px' },
  { field: 'objectName', headerName: 'Object', width: '110px' },
  { field: 'fieldType', headerName: 'Type', width: '100px' },
];

const renderRows = ({
  items,
  activeItemId,
  section = 'dependencies',
  ObjectNameLabelWrapper,
  LabelWrapperComponent,
  dataTableType = 'fields',
}: Pick<FieldTableProps, 'items' | 'activeItemId' | 'section' | 'dataTableType'> & {
  ObjectNameLabelWrapper: ReturnType<typeof createStableLabelWrapper>;
  LabelWrapperComponent: ReturnType<typeof createStableLabelWrapper>;
}): DataTableRow[] => {
  return items.map((row, index) => {
    return {
      id: row.id,
      label: (
        <FieldLabelWithNameInTooltip
          key={row.id}
          label={row.label}
          name={row.name}
          LabelWrapperComponent={LabelWrapperComponent}
          labelWrapperComponentProps={{ rowIndex: index }}
        />
      ),
      name: row.name,
      isRowActive: activeItemId === row.id,
      _objectNameStr: row.objectApiNames?.[0], //needed in onRowClick
      objectName: (
        <ObjectTypeLabelAndKeyTag
          objectType={row.objectApiNames?.[0]}
          LabelWrapperComponent={ObjectNameLabelWrapper}
          labelWrapperComponentSx={markStyles}
          labelWrapperComponentProps={{ rowIndex: index }}
        />
      ), //displayed in table
      fieldType: (
        <TableTypography>
          <TableItemHighlightedText
            text={
              FunnelFieldLabels[row.fieldType as unknown as SweepFieldTypes] ?? row.fieldType ?? ''
            }
            field="fieldType"
            section={section}
            tableType={dataTableType}
            rowIndex={index}
          />
        </TableTypography>
      ), //TS is complaining without unknown but the conversion is correct
      isClickable: true,
      isRollup: row.isRollup,
    };
  });
};

export const FieldsTable = ({
  items,
  onFieldClick,
  clearHistory,
  activeItemId,
  section = 'dependencies',
  dataTableType = 'fields',
  ...props
}: FieldTableProps) => {
  // Create stable wrapper components ONCE per table, not per row
  const ObjectNameLabelWrapper = useMemo(
    () =>
      createStableLabelWrapper({
        field: 'objectApiNames',
        section,
        tableType: dataTableType,
      }),
    [section, dataTableType],
  );

  const LabelWrapperComponent = useMemo(
    () =>
      createStableLabelWrapper({
        field: 'label',
        section,
        tableType: dataTableType,
      }),
    [section, dataTableType],
  );

  const onRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, row: DataTableRow) => {
      onFieldClick({
        id: row.id ?? '',
        name: row.name,
        objectName: row._objectNameStr,
        parentType: row.isRollup ? ConfigurationType.rollups : ConfigurationType.fields,
        clearHistory,
        contentType: DependenciesContentViewType.default,
      });
    },
    [onFieldClick, clearHistory],
  );

  const rows = renderRows({
    items,
    activeItemId,
    section,
    ObjectNameLabelWrapper,
    LabelWrapperComponent,
    dataTableType,
  });

  return (
    <TableWithHeader
      {...props}
      key="fields"
      columns={fieldsColumns}
      rows={rows}
      onRowClick={onRowClick}
      dataTableType={dataTableType}
      section={section}
    />
  );
};
